import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Button, Group, Loader, Stack, Text } from '@mantine/core';
import { WidgetFrame, type WidgetProps } from '@ayocore/platform/client/widgets';
import { useDealReportRepository } from '../../../rpc';
import { buildDealsListUrl } from '../shared/buildDealsListUrl';
import { DealStatusAmounts } from '../shared/DealStatusAmounts';
import { crmWidgetStyle } from '../shared/presentation';
import { UserDealsWidgetController } from './UserDealsWidgetController';

/** Суммы личных сделок по статусам. */
export function UserDealsWidget({ title = 'Твои сделки', className }: WidgetProps) {
  const repository = useDealReportRepository();
  const [controller] = useState(() => new UserDealsWidgetController(repository));
  const { stats, loading, error } = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot
  );

  useEffect(() => {
    void controller.load();
    return () => controller.cancelLoad();
  }, [controller]);

  let content: ReactNode;
  if (loading) {
    content = (
      <Group gap='xs' role='status'>
        <Loader size='xs' />
        <Text size='sm' c='dimmed'>
          Загрузка сделок…
        </Text>
      </Group>
    );
  } else if (error) {
    content = (
      <Stack gap='xs'>
        <Text size='sm' c='red' role='alert'>
          {error}
        </Text>
        <Button
          size='xs'
          variant='light'
          onClick={() => void controller.load()}
          style={{ alignSelf: 'flex-start' }}
        >
          Повторить
        </Button>
      </Stack>
    );
  } else if (stats.length === 0) {
    content = (
      <Text size='sm' c='dimmed'>
        Нет сделок для отображения
      </Text>
    );
  } else {
    content = (
      <DealStatusAmounts
        items={stats}
        getStatusUrl={statusId => buildDealsListUrl({ statusId, mine: true })}
      />
    );
  }

  return (
    <WidgetFrame title={title} className={className} radius='lg' style={crmWidgetStyle}>
      {content}
    </WidgetFrame>
  );
}
